/**
 * Iter 250 · Team roster for Clients & Brands.
 *
 * Shared UI for /workspaces/{ws}/clients/{cid}/members and
 * /workspaces/{ws}/brands/{bid}/members. Admins can add, change role,
 * and remove members via SearchableSelect + role dropdown.
 *
 * Props:
 *   - scope: "client" | "brand"
 *   - wsId: workspace id
 *   - recordId: the client_id or brand_id
 *   - canManage: boolean (from perm check) — hides Add form when false
 */
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Trash2, Users } from "lucide-react";
import MultiSelect from "@/components/ui/multi-select";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { apiErrorMessage } from "@/lib/apiError";

const ROLE_OPTIONS = [
  { value: "manager", label: "Manager" },
  { value: "member", label: "Member" },
  { value: "viewer", label: "Viewer" },
];

export default function TeamRosterTab({ scope, wsId, recordId, canManage = true }) {
  const [members, setMembers] = useState([]);
  const [workspaceMembers, setWorkspaceMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  // Feb 2026 R101 · Multi-select: user can pick several teammates in one
  // shot; Add batches individual POSTs (backend keeps one-user-per-call
  // semantics and returns fast). `newUserIds` is a string[].
  const [newUserIds, setNewUserIds] = useState([]);
  const [newRole, setNewRole] = useState("member");
  const [saving, setSaving] = useState(false);

  const base = `/workspaces/${wsId}/${scope}s/${recordId}/members`;

  const load = useCallback(async () => {
    if (!wsId || !recordId) return;
    setLoading(true);
    try {
      const [{ data: ms }, { data: wms }] = await Promise.all([
        api.get(base),
        api.get(`/workspaces/${wsId}/members`),
      ]);
      setMembers(ms || []);
      setWorkspaceMembers(wms || []);
    } catch {
      toast.error("Failed to load team roster");
    } finally { setLoading(false); }
  }, [base, wsId, recordId]);

  useEffect(() => { load(); }, [load]);

  const currentIds = new Set(members.map((m) => m.user_id));
  const availableOpts = (workspaceMembers || [])
    .filter((w) => w.status === "active" && !currentIds.has(w.user_id))
    .map((w) => ({
      value: w.user_id,
      label: w.user?.name || w.user?.email || "Unknown",
      hint: w.user?.email && (w.user?.name ? w.user.email : null),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const addMember = async () => {
    if (!newUserIds.length) return;
    setSaving(true);
    let ok = 0;
    const failed = [];
    for (const uid of newUserIds) {
      try {
        await api.post(base, { user_id: uid, role: newRole });
        ok += 1;
      } catch (e) {
        failed.push(uid);
        console.error("Add member failed", uid, e?.response?.data?.detail);
      }
    }
    setNewUserIds([]);
    setNewRole("member");
    if (ok > 0) toast.success(`${ok} member${ok === 1 ? "" : "s"} added${failed.length ? ` · ${failed.length} failed` : ""}`);
    else if (failed.length) toast.error("Failed to add members");
    await load();
    setSaving(false);
  };

  const removeMember = async (uid) => {
    if (!window.confirm("Remove this member from the team?")) return;
    try {
      await api.delete(`${base}/${uid}`);
      toast.success("Member removed");
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed"));
    }
  };

  const changeRole = async (uid, role) => {
    try {
      await api.post(base, { user_id: uid, role });
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed"));
    }
  };

  return (
    <div className="space-y-4" data-testid={`${scope}-team-roster`}>
      <div className="flex items-center gap-2 text-sm">
        <Users className="w-4 h-4 text-primary" />
        <div>
          <div className="font-semibold">Team roster</div>
          <div className="text-xs text-muted-foreground">
            Members added here can see this {scope} in their sidebar even without the
            <code className="mx-1 font-mono text-[11px]">{scope}.view_all</code>permission.
          </div>
        </div>
      </div>

      {canManage && (
        <div className="border border-border bg-muted/20 p-3 flex flex-wrap items-end gap-3" data-testid={`${scope}-team-add-row`}>
          <div className="flex-1 min-w-[280px]">
            <div className="label-eyebrow mb-1">Add teammates</div>
            <MultiSelect
              options={availableOpts.map((o) => ({ value: o.value, label: o.label }))}
              value={newUserIds}
              onChange={setNewUserIds}
              placeholder="Search & pick teammates…"
              allLabel="Pick teammates"
              maxChips={2}
              width="w-full"
              testid={`${scope}-team-add-search`}
            />
          </div>
          <div>
            <div className="label-eyebrow mb-1">Role</div>
            <Select value={newRole} onValueChange={setNewRole}>
              <SelectTrigger className="h-9 w-32 text-sm" data-testid={`${scope}-team-add-role`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <button
            type="button"
            onClick={addMember}
            disabled={!newUserIds.length || saving}
            className="h-9 px-3 border border-primary bg-primary text-primary-foreground text-sm hover:bg-primary/90 disabled:opacity-50 flex items-center gap-1.5"
            data-testid={`${scope}-team-add-btn`}
          >
            <UserPlus className="w-3.5 h-3.5" /> Add{newUserIds.length > 1 ? ` (${newUserIds.length})` : ""}
          </button>
        </div>
      )}

      <div className="border border-border" data-testid={`${scope}-team-list`}>
        <div className="grid grid-cols-[1fr_140px_60px] gap-2 px-3 py-2 border-b border-border bg-muted/30 text-[10px] uppercase tracking-widest text-muted-foreground">
          <div>Member</div>
          <div>Role</div>
          <div className="text-right">Actions</div>
        </div>
        {loading && <div className="px-3 py-6 text-sm text-muted-foreground text-center">Loading…</div>}
        {!loading && members.length === 0 && (
          <div className="px-3 py-6 text-sm text-muted-foreground text-center" data-testid={`${scope}-team-empty`}>
            No members yet. {canManage ? "Add a teammate above to give them access." : ""}
          </div>
        )}
        {!loading && members.map((m) => (
          <div key={m.id}
                className="grid grid-cols-[1fr_140px_60px] gap-2 px-3 py-2 border-b border-border last:border-b-0 items-center text-sm hover:bg-muted/20"
                data-testid={`${scope}-team-row-${m.user_id}`}>
            <div className="min-w-0">
              <div className="font-medium truncate">{m.user?.name || m.user?.email || m.user_id}</div>
              {m.user?.name && m.user?.email && <div className="text-[11px] text-muted-foreground truncate">{m.user.email}</div>}
            </div>
            <div>
              {canManage ? (
                <Select value={m.role} onValueChange={(v) => changeRole(m.user_id, v)}>
                  <SelectTrigger className="h-8 text-xs" data-testid={`${scope}-team-role-${m.user_id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <span className="text-xs capitalize">{m.role}</span>
              )}
            </div>
            <div className="text-right">
              {canManage && (
                <button
                  type="button"
                  onClick={() => removeMember(m.user_id)}
                  className="text-muted-foreground hover:text-rose-600 p-1"
                  data-testid={`${scope}-team-remove-${m.user_id}`}
                  title="Remove"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
